import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import PageTableLayout from "../../../components/PageTableLayout";
import apiService from "../../../services/apiService";
import {
  FaFilter,
  FaTimes,
  FaEye,
  FaPencilAlt,
  FaUpload,
  FaFileUpload,
} from "react-icons/fa";
import { Button as UIButton } from "../../../components/ui";
import {
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  Input,
  Popover,
  PopoverContent,
  PopoverHandler,
  Typography,
  Button,
} from "@material-tailwind/react";
import { BiRefresh } from "react-icons/bi";
import { MdDelete } from "react-icons/md";
import { useToast } from "../../../context/ToastContext";
import { GrStatusInfo } from "react-icons/gr";

const breadcrumb = (
  <span>
    Home &nbsp;/&nbsp; <span className="text-gray-700">Task</span>
  </span>
);

const TaskList = () => {
  const navigate = useNavigate();
  const { showSuccess, showError, showInfo, showWarning } = useToast();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [filters, setFilters] = useState({
    title: "",
    status: "",
  });
  const [limit, setLimit] = useState(10);
  const [isFiltered, setIsFiltered] = useState(false);
  const [open, setOpen] = useState(false);
  const [taskId, setTaskId] = useState(null);
  const [title, setTitle] = useState(null);
  const [openDelete, setOpenDelete] = useState(false);
  const [row, setRow] = useState();
  const [statusOpen, setStatusOpen] = useState(false);
  const [transferLoadingId, setTransferLoadingId] = useState(null);
  function handleStatusOpen() {
    setStatusOpen(!statusOpen);
  }
  const handleTransferToEdukit = async (transferRow) => {
    const taskQuestionId = transferRow?.id || transferRow?._id;
    const aiQsetId =
      transferRow?.aiQset?.id ||
      transferRow?.aiQset?._id ||
      transferRow?.aiQset;

    const originalTaskId =
      transferRow?.taskId?.id ||
      transferRow?.taskId?._id ||
      transferRow?.taskId ||
      "";

    if (!taskQuestionId || !aiQsetId) {
      showError("Task question or AI QSet id not found");
      return;
    }

    try {
      setTransferLoadingId(taskQuestionId);

      const formData = new FormData();
      formData.append("taskQuestion", String(taskQuestionId));
      formData.append("taskQuestionId", String(taskQuestionId));
      formData.append("aiQset", String(aiQsetId));
      formData.append("qSet", String(aiQsetId));

      if (originalTaskId) {
        formData.append("taskId", String(originalTaskId));
      }

      const response = await apiService.request("/i/ims/transfer/edukit", {
        method: "POST",
        body: formData,
      });

      showSuccess(response?.message || "Transfer To Edukit request sent");
    } catch (err) {
      console.log("TRANSFER_TO_EDUKIT_FAILED:", err);
      showError("Transfer To Edukit failed");
    } finally {
      setTransferLoadingId(null);
    }
  };
  function handleOpen() {
    setTaskId(null);
    setTitle(null);
    setRow(row);
    setOpen((value) => !value);
  }

  function handleOpenDelete() {
    setTaskId(null);
    setTitle(null);
    setRow(row);
    setOpenDelete((value) => !value);
  }

  function handleDelete(deletedRow) {
    const newRows = data.filter((el) => el.id != deletedRow.id);
    setData(newRows);
  }

  function handleEditRow(editedRow) {
    const newRows = data.forEach((row) => {
      if (editedRow.id === row.id) {
        return editedRow;
      } else {
        return row;
      }
    });
    setData(newRows);
  }

  const fetchData = useCallback(
    async (page, filterParams = {}, itemsPerPage = limit) => {
      try {
        setLoading(true);
        const params = new URLSearchParams({
          page,
          limit: itemsPerPage,
          totalCount: true,
          populateTemplate: true,
        });
        Object.entries(filterParams).forEach(([key, value]) => {
          if (value) {
            params.append(key, value);
          }
        });

        const response = await apiService.request_old(
          `/i/ims/taskquestion?${params.toString()}`,
        );
        setData(response.data);
        setTotalRecords(response.totalCount);
        setCurrentPage(page);
        setIsFiltered(
          Object.values(filterParams).some((value) => value !== ""),
        );
      } catch (error) {
        console.error("Error fetching Tasks:", error);
      } finally {
        setLoading(false);
      }
    },
    [limit],
  );

  useEffect(() => {
    fetchData(1);
  }, [fetchData]);
  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };
  const handleFilterSubmit = () => {
    fetchData(1, filters);
  };
  const handleFilterReset = () => {
    const resetFilters = {
      title: "",
      status: "",
    };
    setFilters(resetFilters);
    fetchData(1, resetFilters);
  };
  const handlePageChange = (page) => {
    fetchData(page, filters);
  };
  const handleLimitChange = (value) => {
    const newLimit = Number(
      typeof value === "object" ? value.target.value : value,
    );
    setLimit(newLimit);
    fetchData(1, filters, newLimit);
  };

  const actionButtons = [
    {
      label: "Test",
      className: "bg-gray-50 cursor-default",
    },
  ];

  const columns = [
    {
      key: "title",
      title: "Name",
      render: (row) => {
        return <label>{row?.taskId?.title || "-"}</label>;
      },
      filterComponent: (
        <input
          type="text"
          className="w-full border px-2 py-1 rounded text-xs"
          placeholder="Filter by name"
          value={filters.title}
          onChange={(e) => handleFilterChange("title", e.target.value)}
        />
      ),
    },
    {
      key: "template",
      title: "Template",
      render: (row) => {
        const templateTitle =
          row?.taskId?.template?.value?.title ||
          row?.taskId?.template?.title ||
          row?.taskId?.template?.value ||
          "-";

        return <label>{templateTitle}</label>;
      },
    },
    {
      key: "dueDate",
      title: "Due Date",
      render: (row) => {
        const dueDate = row?.taskId?.dueDate;

        if (!dueDate) {
          return "-";
        }

        const date = new Date(dueDate);

        if (isNaN(date.getTime())) {
          return "-";
        }

        return date.toLocaleString("en-IN", {
          timeZone: "Asia/Kolkata",
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      },
    },
    {
      key: "status",
      title: "Status",
      render: (row) => {
        return <label>{row?.taskId?.status || "-"}</label>;
      },
      filterComponent: (
        <select
          className="w-full border px-2 py-1 rounded text-xs"
          value={filters.status}
          onChange={(e) => handleFilterChange("status", e.target.value)}
        >
          <option value="">All Status</option>

          {(
            data?.[0]?.taskId?.template?.value?.status || ["Pending", "Done"]
          ).map((status) => (
            <option key={status} value={status}>
              {status}
            </option>
          ))}
        </select>
      ),
    },
    {
      key: "actions",
      title: "Actions",
      render: (row) => {
        const aiQsetId = row?.aiQset?.id || row?.aiQset?._id || row?.aiQset;
        const rowId = row?.id || row?._id;
        const isTransferLoading = transferLoadingId === rowId;

        return (
          <div className="flex space-x-2">
            <UIButton
              onClick={(e) => {
                e.stopPropagation();
                navigate(`view/${row.id}`);
              }}
              variant="outline"
              size="xs"
              title="View"
              className="text-blue-600 border-blue-600 hover:bg-blue-50"
            >
              <FaEye className="h-4 w-4" />
            </UIButton>

            {/* <UIButton
              onClick={(e) => {
                e.stopPropagation();
                setTaskId(row?.id);
                setTitle(row?.taskId?.title);
                setOpenDelete(true);
                setRow(row);
              }}
              variant="outline"
              size="xs"
              title="Delete"
              className="text-amber-600 border-amber-600 hover:bg-amber-50"
            >
              <MdDelete className="h-4 w-4" />
            </UIButton> */}

            {aiQsetId && (
              <UIButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleTransferToEdukit(row);
                }}
                variant="outline"
                size="xs"
                title="Transfer To Edukit"
                disabled={isTransferLoading}
                className="text-green-600 border-green-600 hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FaUpload className="h-4 w-4" />
                <span className="ml-1">
                  {isTransferLoading ? "Sending..." : "Transfer"}
                </span>
              </UIButton>
            )}
          </div>
        );
      },
      filterComponent: (
        <div className="flex space-x-1">
          <UIButton
            variant="outline"
            size="xs"
            className="text-blue-600 border-blue-600 hover:bg-blue-50"
            onClick={handleFilterSubmit}
          >
            <FaFilter className="h-3 w-3 mr-1" />
            Filter
          </UIButton>

          <UIButton
            variant="outline"
            size="xs"
            className="text-red-600 border-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleFilterReset}
            disabled={!isFiltered}
          >
            <FaTimes className="h-3 w-3 mr-1" />
            Reset
          </UIButton>

          <UIButton
            variant="outline"
            size="xs"
            className="text-blue-600 border-blue-600 hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => fetchData(1)}
          >
            <BiRefresh className="h-3 w-3 mr-1" />
            Refresh
          </UIButton>
        </div>
      ),
    },
  ];
  const handleView = (row) => {
    navigate(`/task/view/${row.id}`);
  };

  const handleEdit = (row) => {
    navigate(`/qSet/edit/${row.id}`);
  };

  return (
    <>
      <PageTableLayout
        title="Tasks"
        breadcrumb={breadcrumb}
        columns={columns}
        data={data || []}
        actions={actionButtons}
        loading={loading}
        pagination={{
          currentPage,
          totalRecords,
          totalPages: Math.ceil(totalRecords / limit),
          limit,
          onLimitChange: handleLimitChange,
        }}
        onPageChange={handlePageChange}
        showFiltersInTable={true}
      />
      <EditModal
        open={open}
        handleOpen={handleOpen}
        taskId={taskId}
        qName={title}
        handleEditRow={handleEditRow}
        row={row}
      />
      <DeleteModal
        openDelete={openDelete}
        handleOpen={handleOpenDelete}
        taskId={taskId}
        title={title}
        handleDelete={handleDelete}
        row={row}
      />
    </>
  );
};

const EditModal = ({ open, handleOpen, qName, taskId, handleEditRow, row }) => {
  const [qSetName, setQSetName] = useState(qName);

  useEffect(() => {
    setQSetName(qName);
  }, [qName]);

  async function handleSubmit() {
    let response;
    const formData = new FormData();
    formData.append("title", qSetName);

    try {
      response = await apiService.request_old(`/i/ims/aiqset/${taskId}`, {
        method: "PUT",
        body: formData,
      });

      handleEditRow(row);
    } catch (err) {
      console.log(err);
    }

    handleOpen();
  }

  return (
    <Dialog
      size="sm"
      open={open}
      handler={handleOpen}
      className="bg-transparent shadow-none bg-white"
    >
      <DialogHeader>
        <Typography variant="h4" color="blue-gray">
          Edit Your Question Set
        </Typography>
      </DialogHeader>
      <DialogBody>
        <Typography className="mb-3 font-normal">
          Question Set Name : {qSetName}
        </Typography>
        <Input
          value={qSetName}
          onChange={(e) => setQSetName(e.target.value)}
          label="Question Set Name"
        ></Input>
      </DialogBody>

      <DialogFooter>
        <Button
          onClick={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          Submit
        </Button>
      </DialogFooter>
    </Dialog>
  );
};

const DeleteModal = ({
  openDelete,
  handleOpen,
  taskId,
  title,
  handleDelete,
  row,
}) => {
  async function deleteQSet() {
    try {
      await apiService.request_old(`/i/ims/taskquestion/${taskId}`, {
        method: "DELETE",
      });

      handleDelete(row);
    } catch (err) {
      console.log(err);
      window.alert("Failed to delete");
    }
    handleOpen();
  }

  return (
    <Dialog
      size="sm"
      open={openDelete}
      handler={handleOpen}
      className="bg-transparent shadow-none bg-white"
    >
      <DialogHeader>
        <Typography variant="h3" color="red">
          Warning!!
        </Typography>
      </DialogHeader>
      <DialogBody>
        <Typography className="mb-3 font-normal">
          Are you sure you want to delete the Task {title} ?
        </Typography>
      </DialogBody>
      <DialogFooter className="flex justify-center">
        <div className="flex gap-3">
          <Button
            onClick={() => deleteQSet()}
            className="bg-red-600 hover:bg-red-700"
          >
            Delete
          </Button>
          <Button onClick={() => handleOpen()}>Cancel</Button>
        </div>
      </DialogFooter>
    </Dialog>
  );
};

export default TaskList;
